import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  FileText,
  Download,
  Upload as UploadIcon,
  Loader2,
  X,
} from "lucide-react";
import {
  useDownloadCsvTemplate,
  useImportEntities,
  useValidateCsv,
} from "@/hooks/use-import";
import {
  SAMPLE_ROW_COUNT,
  countCsvRows,
  sampleCsvFile,
} from "@/lib/csv-sample";
import type { CsvValidationResponse, ImportEntityType } from "@adva/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { StepCounter } from "../components/step-counter";
import { formatEntityType } from "../../labels";

const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface StepUploadProps {
  entityType: ImportEntityType | null;
  file: File | null;
  onSelectEntity: (et: ImportEntityType) => void;
  onSelectFile: (file: File) => void;
  onClearFile: () => void;
  onValidated: (result: CsvValidationResponse, totalRows: number) => void;
  onError: (message: string) => void;
}

export function StepUpload({
  entityType,
  file,
  onSelectEntity,
  onSelectFile,
  onClearFile,
  onValidated,
  onError,
}: StepUploadProps) {
  const { data: entitiesData, isLoading: entitiesLoading } =
    useImportEntities();
  const validateMutation = useValidateCsv();
  const downloadTemplate = useDownloadCsvTemplate();

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length === 0) return;
      const picked = accepted[0];
      if (picked.size > MAX_BYTES) {
        onError(`File too large (${formatBytes(picked.size)}). Max 25 MB.`);
        return;
      }
      onSelectFile(picked);
    },
    [onSelectFile, onError],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
    },
    multiple: false,
    maxSize: MAX_BYTES,
    disabled: !entityType,
  });

  const handleValidate = async () => {
    if (!file || !entityType) return;
    try {
      const sampled = await sampleCsvFile(file, SAMPLE_ROW_COUNT);
      const totalRows = await countCsvRows(file);
      const result = await validateMutation.mutateAsync({
        entityType,
        file: sampled,
      });
      onValidated(result, totalRows);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Validation failed");
    }
  };

  const handleDownloadTemplate = async () => {
    if (!entityType) return;
    try {
      await downloadTemplate.mutateAsync(entityType);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Template download failed");
    }
  };

  // Sort entities by tier, then alphabetically by entity_type, so the
  // dropdown follows the dependency order (tier 1 first).
  const sortedEntities = (entitiesData?.entities ?? [])
    .slice()
    .sort(
      (a, b) => a.tier - b.tier || a.entity_type.localeCompare(b.entity_type),
    );

  return (
    <div className="space-y-5">
      <StepCounter current={1} total={4} label="Upload CSV" />

      <div className="space-y-2">
        <label
          htmlFor="entity-type"
          className="typo-label text-[color:var(--color-text-md)]"
        >
          Entity type
        </label>
        <Select
          value={entityType ?? undefined}
          onValueChange={(v) => onSelectEntity(v as ImportEntityType)}
          disabled={entitiesLoading}
        >
          <SelectTrigger id="entity-type" className="w-full">
            <SelectValue
              placeholder={
                entitiesLoading
                  ? "Loading entity types…"
                  : "Choose what this CSV contains"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {sortedEntities.map((entity) => (
              <SelectItem key={entity.entity_type} value={entity.entity_type}>
                {formatEntityType(entity.entity_type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="typo-body text-[color:var(--color-text-md)]">
          Tiers run in order — products and territories before customers,
          customers before opportunities.
        </p>
      </div>

      {entityType && (
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-2 typo-body text-[color:var(--color-accent)] hover:underline"
        >
          <Download className="h-4 w-4" />
          Download {formatEntityType(entityType)} CSV template
        </button>
      )}

      {!file ? (
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
            !entityType
              ? "border-[color:var(--color-border)] opacity-50 cursor-not-allowed"
              : isDragActive
                ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5 cursor-pointer"
                : "border-[color:var(--color-border)] hover:border-[color:var(--color-accent)]/50 cursor-pointer"
          }`}
        >
          <input {...getInputProps()} />
          <UploadIcon className="h-10 w-10 text-[color:var(--color-text-lo)] mb-3" />
          <p className="font-medium text-[color:var(--color-text-hi)]">
            {!entityType
              ? "Choose an entity type first"
              : isDragActive
                ? "Drop it here"
                : "Drop your CSV here, or click to select"}
          </p>
          <p className="typo-body mt-1 text-[color:var(--color-text-md)]">
            CSV only · 25 MB max
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-[color:var(--color-text-lo)]" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{file.name}</p>
              <p className="typo-body text-[color:var(--color-text-md)]">
                {formatBytes(file.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClearFile}
              className="rounded p-1 hover:bg-[color:var(--color-elevated)]"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!file || !entityType || validateMutation.isPending}
          onClick={handleValidate}
        >
          {validateMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Continue
        </Button>
      </div>
    </div>
  );
}
